import React from 'react';
import styled from 'styled-components';
import { format } from 'date-fns-tz';
import DoughnutGauge from 'areas/dashboard/components/DoughnutGauge';

import StatusLabel from 'common/components/StatusLabel';
import { Icon } from 'semantic-ui-react';
import { ASSESSMENT_PERIOD_IN_PROGRESS } from '../constants';

type AssessmentSurveySummaryCardProps = {
  data: any;
  debriefSchedule: any;
};

const AssessmentSurveySummaryCard = ({
  data,
  debriefSchedule,
}: AssessmentSurveySummaryCardProps) => {

  const getSurveyStatusLabel = (status: number) => {
    const now = Date.now();
    const closeAt = debriefSchedule.close_at ? new Date(debriefSchedule.close_at).getTime() : null;

    let color = '#9e9e9e';
    let label = 'Unknown';

    switch (status) {
      case 1:
        label = 'Scheduled';
        color = '#7A7A7A';
        break;
      case 3:
        label = 'In Progress';
        color = '#1976d2';
        break;
      case 5:
        label = 'Completed';
        color = '#2e7d32';
        break;
      case 9:
        label = 'Cancelled';
        color = '#424242';
        break;
    }

    // Special case: schedule is live but end date passed
    if (debriefSchedule.status === ASSESSMENT_PERIOD_IN_PROGRESS && closeAt && closeAt < now) {
      return <StatusLabel color="#ef6c00" title="Awaiting Closure">Awaiting Closure</StatusLabel>;
    }

    return <StatusLabel color={color}>{label}</StatusLabel>;
  };

  function countEmailFailures(schedule: any): number {
    const participants = (schedule as any).participants as {
      lastEmailSuccessful?: boolean | null;
    }[] | undefined;

    if (!Array.isArray(participants)) return 0;

    // Count participants whose most recent email attempt failed
    return participants.filter(p => p.lastEmailSuccessful === false).length;
  }

  const invitationIssuesCount = countEmailFailures(debriefSchedule);


  return (
    <ColumnContainer>
      {/* Column 1 */}
      <Column flex={1}>
        <DoughnutGauge
          complete={
            data?.total_requests > 0 ? (data?.total_responses / data?.total_requests) * 100 : 0
          }
          percent={true}
          size="medium"
        />
      </Column>

      {/* Column 2 */}
      <Column flex={2}>
        <InnerContainer>
          <Grid>
            <LabelValue>Status</LabelValue>
            {getSurveyStatusLabel(debriefSchedule.status)}
            {debriefSchedule?.scheduled_for && (
              <>
                <LabelValue>Start</LabelValue>
                <Value>
                  {format(new Date(debriefSchedule.scheduled_for), 'MMM. d, yyyy')}{' '}
                  {format(new Date(debriefSchedule.scheduled_for), 'h:mm a')}
                </Value>
              </>
            )}

            {debriefSchedule?.close_at && (
              <>
                <LabelValue>End</LabelValue>
                <Value>
                  {format(new Date(debriefSchedule.close_at), 'MMM. d, yyyy')}{' '}
                  {format(new Date(debriefSchedule.close_at), 'h:mm a')}
                </Value>
              </>
            )}
          </Grid>
        </InnerContainer>
      </Column>
      {/* Column 3 */}
      <Column flex={2}>
        <InnerContainer>
          <Grid>
            <LabelValue>Invitations</LabelValue>
            <Value>{data?.total_requests}</Value>

            <LabelValue>Responses</LabelValue>
            <Value>{data?.total_responses}</Value>

            <LabelValue>Invitation Issues</LabelValue>
            <Value>{invitationIssuesCount} {invitationIssuesCount > 0 && (
              <Icon
                name={'warning sign'}
                color={'orange'}
                size='small'
                title="Emails were not delivered to some participants."
              />

            )}</Value>
          </Grid>
        </InnerContainer>
      </Column>
    </ColumnContainer>
  );
};
export default AssessmentSurveySummaryCard;

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: align-start;
  width: 100%;
`;

const Column = styled.div<{ flex?: number }>`
  display: flex;
  flex-direction: column;
  font-size: 12px;
  align-items: flex-start;
  justify-content: ${props => (props.flex === 1 ? 'flex-start' : 'flex-start')};
  flex: ${props => props.flex || 1};
  max-width: ${props => (props.flex === 1 ? '120px' : '500px')};
  min-width: ${props => (props.flex === 1 ? '100px' : '300px')};
`;

const InnerContainer = styled.div`
  padding-left: 20px;
  padding-top: 10px;
  width: 100%;
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  gap: 3px;
  width: 100%;
  align-items: center;  
  justify-items: start;   
`;

const LabelValue = styled.span<{ color?: string }>`
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 500;
  align-self: center;
  text-align: left;
  font-size: 14px;
  line-height: 18px;
  color: #333;
  margin-bottom: 5px;
  `;
const Value = styled.span<{ color?: string }>`
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 400;
  align-self: center;
  text-align: left;
  font-size: 14px;
  line-height: 18px;
  color: #333;
  margin-bottom: 5px;
  `;


